/**
 * Builder > Conditions > Add button
 * <ua-builder-conditions-add-button>
 */

// Import media
import filterBannerImg from './img/filter-banner.svg';

// Import light DOM CSS
import './style.scss';

// Import dependencies
import { html, LitElement, render } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';

// Import global utils
import { isEmpty } from '@automator/shared/utils/is-empty';
import { addParametersToURL } from '@automator/shared/utils/add-parameters-to-url';
import { addParametersUTM } from '@automator/shared/utils/add-parameters-utm-to-url';

// Import escapeHTML
import { escapeHTML } from '@wordpress/escape-html';

// Import i18n functions
import { __, _x } from '@wordpress/i18n';

// Import utilities and components
import { getConditionsGroupById } from '@automator/builder/_legacy/js/item/conditions/utilities';
import ConditionsGroup from '@automator/builder/_legacy/js/item/conditions/conditions-group';
import ItemConditionsModalList from '@automator/builder/_legacy/js/item/conditions/modal-list';

// eslint-disable-next-line require-jsdoc
@customElement( 'ua-builder-conditions-add-button' )
export class ConditionAddButton extends LitElement {
	/**
	 * Properties
	 */

	// The parent ID of the filter block
	@property( { type: Number, attribute: 'parent-id' } ) accessor parentID = UncannyAutomator._recipe.recipe_id;

	// optional. If this is defined, the item will be added to the conditions block
	@property( { type: String, attribute: 'action-id' } ) accessor actionID; 
	// optional. If this is defined, the condition will be added to that conditions group instead of creating another
	@property( { type: String, attribute: 'condition-group-id' } ) accessor conditionsGroupID;

	// optional. The size of the button
	@property( { type: String } ) accessor size; 
	// optional. The style of the button
	@property( { type: String } ) accessor color = 'secondary';
	@property( { type: Boolean, reactive: true } ) accessor isActionOpen = false;


	// eslint-disable-next-line require-jsdoc
	render() {
		return this._templateButton;
	}

	/**
	 * Handles the click event on the button
	 * 
	 * @return {undefined}
	 */
	handleClick() {
		// Check if the user is editing an action and the action's options are open. If so, don't do anything
		if ( this.isActionOpen ) {
			return;
		}

		// If the user doesn't have Pro, show the upsell dialog
		if ( ! UncannyAutomatorBackend._site.automator.is_pro_active ) {
			this.handleCreateUpsellDialog();
			return;
		}

		// Otherwise, add the filter
		this.handleAddFilter();
	}

	/**
	 * The button template with or without the tooltip
	 * 
	 * @return {TemplateResult} The button template
	 */
	get _templateButton() {
		const $button = html`
			<uo-button
				color=${ ifDefined( this.color ) }
				size=${ ifDefined( this.size ) }
				
				?disabled=${ this.shouldDisableButton }

				@click=${ this.handleClick }
			>
				${ this._templateButtonLabel }
			</uo-button>
		`;

		// Check if we should add a tooltip
		if ( 
			// Has pro
			UncannyAutomatorBackend._site.automator.is_pro_active &&
			// Options are open
			this.isActionOpen
		) {
			return html`
				<uo-tooltip
					@click=${ this.handleClick }
				>
					${ escapeHTML( __( 'Please save the action first', 'uncanny-automator' ) ) }

					<div slot="target">
						${ $button }
					</div>
				</uo-tooltip>
			`;
		}

		return $button;
	}

	/**
	 * The button label. If the user has the Pro version, it will just show the slot content. If not, it will show the slot content and the Pro tag as well.
	 * 
	 * @return {TemplateResult} The button label
	 */
	get _templateButtonLabel() {
		// Check if the user has the Pro version
		if ( UncannyAutomatorBackend._site.automator.is_pro_active ) {
			return html`<slot></slot>`;
		}

		// Append the Pro tag
		return html`<slot></slot> <uo-pro-tag size=${ ifDefined( this.size ) }></uo-pro-tag>`;
	}

	/**
	 * Checks if the button should be disabled
	 * 
	 * @returns {Boolean} Whether the button should be disabled
	 */
	get shouldDisableButton() {
		// Disable it only if the options are open
		return this.isActionOpen;
	}

	// eslint-disable-next-line require-jsdoc
	connectedCallback() {
		super.connectedCallback();

		// Disable the button when the item's options is opened
		this.disableWhenUserEditsAction();
	}

	// eslint-disable-next-line require-jsdoc
	disconnectedCallback() {
		super.disconnectedCallback();

		// Remove the event listeners
		document.removeEventListener( 'automator/item/options/open', this.callbackOptionsOpen );
		document.removeEventListener( 'automator/item/options/close', this.callbackOptionsClose );
	}

	/**
	 * Goes to the next step and renders list of conditions
	 */
	showListOfConditions() {
		// Create the conditions group
		const conditionsGroup = ! isEmpty( this.conditionsGroupID ) 
			? getConditionsGroupById( { conditionsGroupId: this.conditionsGroupID } ) 
			: ( new ConditionsGroup( {
				parentID: this.parentID,
			} ) );

		// Check if we should add an action automatically
		if ( ! isEmpty( this.actionID ) ) {
			conditionsGroup.actions = [ this.actionID ];
		}

		// Create the modal with the list of conditions
		new ItemConditionsModalList( {
			conditionsGroupId: conditionsGroup.id,
			comingFrom: 'action-filter-button',
			parentID: this.parentID,
		} );
	}

	/**
	 * Disables the button when the user is editing the action
	 *
	 * @return {undefined}
	 */
	disableWhenUserEditsAction() {
		// Check if this button is on an action in the first place
		if ( isEmpty( this.actionID ) ) {
			return;
		}

		// Check if it can manage conditions
		if ( ! UncannyAutomatorBackend._site.automator.is_pro_active ) {
			return;
		}

		/**
		 * The callback invoked when the options panel is opened
		 * We're following this approach so we can remove the listener when the 
		 * Lit element is removed, as the listener is tied to the document, and not to this node
		 */
		this.callbackOptionsOpen = {
			handleEvent: ( event ) => {
				// Check if it's the action where this button is
				if ( this.actionID != event.detail.itemId ) {
					return;
				}

				// Disable the button
				this.isActionOpen = true;
			}
		};

		// Same as above, but when the panel is closed
		this.callbackOptionsClose = {
			handleEvent: ( event ) => {
				// Check if it's the action where this button is
				if ( this.actionID != event.detail.itemId ) {
					return;
				}

				// Enable the button
				this.isActionOpen = false;
			}
		}

		// Listen when an item is opened
		document.addEventListener( 'automator/item/options/open', this.callbackOptionsOpen );

		// Listen when the item is closed
		document.addEventListener( 'automator/item/options/close', this.callbackOptionsClose );
	}

	/**
	 * Handles the click event on the button.
	 *
	 * This method is responsible for determining what to do when the user clicks the button.
	 * — If the user is not a Pro user, it will show the upsell dialog.
	 * — If the user is a Pro user, it will add a filter to the recipe.
	 *
	 * @return {undefined}
	 */
	handleAddFilter() {
		// Show the list of conditions
		this.showListOfConditions();
	}

	/**
	 * Creates the dialog that explains to non-Pro users what this feature does and how they can get it.
	 */
	handleCreateUpsellDialog() {
		// Create the node that we'll use to render the dialog
		// This node will be added as a direct child of the body node.
		// We can't add it inside the component created on this class because of z-index issues
		const $wrapper = document.createElement( 'div' );
		document.body.append( $wrapper );

		render(
			html`
				<uo-dialog
					heading=${ escapeHTML( __( 'Filter', 'uncanny-automator' ) ) }
					featured-image=${ filterBannerImg }
					featured-image-position="center bottom"
					size="large"
					open
				>
					<p>
						<uo-markdown 
							allowed-tags="strong"
							.content=${ escapeHTML( __( 'Add **conditional logic** to your recipes with filters. With the ability to **run actions only if specific conditions are met**, recipes become much more powerful!', 'uncanny-automator' ) ) }>
						</uo-markdown>
					</p>

					<p>
						${ escapeHTML( __( 'Here are a few examples of what is possible with filters:', 'uncanny-automator' ) ) }
					</p>

					<ul>
						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'In recipes with a form submission trigger, **run different actions depending on the options the user selected on the form**.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'In recipes with a purchase trigger, **run different actions depending on which product the user purchased**.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'In recipes with a course or group enrollment trigger, **run different actions depending on which course or group the user enrolled in**.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>
					</ul>

					<p>
						${ escapeHTML( __( 'You can also use filters to simply give recipes additional "intelligence" about when actions should run, such as:', 'uncanny-automator' ) ) }
					</p>

					<ul>
						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'Whether the user has a **specific role or membership level**.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'Whether the user is **enrolled in a specific course or group**.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>

						<li>
							<uo-markdown 
								allowed-tags="strong"
								.content=${ escapeHTML( __( 'Whether the user has **specific user meta values**.', 'uncanny-automator' ) ) }>
							</uo-markdown>
						</li>
					</ul>
					
					<uo-button
						slot="primary-action"

						href=${ this.upsaleURL }
						target="_blank"

						dialog-action="close"
					>
						${ escapeHTML( __( 'Get Filters', 'uncanny-automator' ) ) }
					</uo-button>

					<uo-button
						slot="secondary-action"
						dialog-action="close"
						color="secondary"
					>
						${ escapeHTML( _x( 'Cancel', 'Verb', 'uncanny-automator' ) ) }
					</uo-button>
				</uo-dialog>
			`,
			$wrapper
		);
	}

	/**
	 * The URL to the upsell page. This URL will include UTM parameters to track the user's journey.
	 *
	 * @return {String} The URL to the upsell page.
	 */
	get upsaleURL() {
		return addParametersToURL(
			// Add UTM parameters
			addParametersUTM( 'https://automatorplugin.com/pricing/', {
				medium: 'recipe_creator',
				content: 'add_filter_button',
			} ),
			// Add context to show banner in the pricing page
			{
				uap_bpro_context: 'filter'
			}
		)
	}
}
